import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Accessibility, ALargeSmall, Contrast, Link2, MousePointer2, Pause, RotateCcw, Type, Underline, X } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const DEFAULTS = {
  textSize: 0,
  contrast: false,
  grayscale: false,
  highlightLinks: false,
  underlineLinks: false,
  readableFont: false,
  pauseMotion: false,
  bigCursor: false,
};

const TOGGLE_CLASS = {
  contrast: "a11y-contrast",
  grayscale: "a11y-grayscale",
  highlightLinks: "a11y-highlightLinks",
  underlineLinks: "a11y-underlineLinks",
  readableFont: "a11y-readableFont",
  pauseMotion: "a11y-pauseMotion",
  bigCursor: "a11y-bigCursor",
};

function OptionButton({ active, onClick, icon: Icon, label, detail, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`flex flex-col items-center gap-1.5 rounded-md border px-2 py-3 text-center transition-[background-color,border-color,color] duration-200 ${
        active ? "border-[#0F4C5C] bg-[#0F4C5C] text-white" : "border-slate-200 bg-slate-50 text-slate-700 hover:border-[#0F4C5C]/40 hover:bg-[#EEF6FC]"
      }`}
      data-testid={testId}
    >
      <Icon className="h-5 w-5" aria-hidden="true" />
      <span className="text-[11px] font-bold leading-tight">{label}</span>
      {detail != null && <span className={`text-[10px] ${active ? "text-slate-200" : "text-slate-500"}`}>{detail}</span>}
    </button>
  );
}

export default function AccessibilityWidget() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(() => {
    try {
      return { ...DEFAULTS, ...JSON.parse(localStorage.getItem("af_a11y") || "{}") };
    } catch {
      return DEFAULTS;
    }
  });

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("a11y-text-1", settings.textSize === 1);
    root.classList.toggle("a11y-text-2", settings.textSize === 2);
    Object.entries(TOGGLE_CLASS).forEach(([key, cls]) => root.classList.toggle(cls, Boolean(settings[key])));
    localStorage.setItem("af_a11y", JSON.stringify(settings));
  }, [settings]);

  const toggle = (key) => setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  const cycleTextSize = () => setSettings((prev) => ({ ...prev, textSize: (prev.textSize + 1) % 3 }));
  const reset = () => setSettings(DEFAULTS);

  return (
    <div className="fixed bottom-20 left-4 z-[60] sm:bottom-6 sm:left-6" data-testid="a11y-widget">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            role="dialog"
            aria-label={t("a11y.title")}
            className="mb-3 w-[320px] max-w-[calc(100vw-2rem)] rounded-lg border border-slate-200 bg-white p-4 shadow-2xl"
            data-testid="a11y-panel"
          >
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-heading flex items-center gap-2 text-base font-bold text-slate-900">
                <Accessibility className="h-5 w-5 text-[#0F4C5C]" aria-hidden="true" />
                {t("a11y.title")}
              </h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t("a11y.close")}
                className="flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                data-testid="a11y-close-btn"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <OptionButton active={settings.textSize > 0} onClick={cycleTextSize} icon={ALargeSmall} label={t("a11y.textSize")} detail={t("a11y.textSizeValues")[settings.textSize]} testId="a11y-text-size-btn" />
              <OptionButton active={settings.contrast} onClick={() => toggle("contrast")} icon={Contrast} label={t("a11y.contrast")} testId="a11y-contrast-btn" />
              <OptionButton active={settings.grayscale} onClick={() => toggle("grayscale")} icon={Type} label={t("a11y.grayscale")} testId="a11y-grayscale-btn" />
              <OptionButton active={settings.highlightLinks} onClick={() => toggle("highlightLinks")} icon={Link2} label={t("a11y.highlightLinks")} testId="a11y-highlight-links-btn" />
              <OptionButton active={settings.underlineLinks} onClick={() => toggle("underlineLinks")} icon={Underline} label={t("a11y.underlineLinks")} testId="a11y-underline-links-btn" />
              <OptionButton active={settings.readableFont} onClick={() => toggle("readableFont")} icon={Type} label={t("a11y.readableFont")} testId="a11y-readable-font-btn" />
              <OptionButton active={settings.pauseMotion} onClick={() => toggle("pauseMotion")} icon={Pause} label={t("a11y.pauseMotion")} testId="a11y-pause-motion-btn" />
              <OptionButton active={settings.bigCursor} onClick={() => toggle("bigCursor")} icon={MousePointer2} label={t("a11y.bigCursor")} testId="a11y-big-cursor-btn" />
              <OptionButton active={false} onClick={reset} icon={RotateCcw} label={t("a11y.reset")} testId="a11y-reset-btn" />
            </div>
            <Link
              to="/accessibility-statement"
              onClick={() => setOpen(false)}
              className="mt-3 block rounded-md border border-[#0F4C5C]/20 bg-[#EEF6FC] px-3 py-2.5 text-center text-xs font-bold text-[#0F4C5C] transition-colors duration-200 hover:bg-[#0F4C5C] hover:text-white"
              data-testid="a11y-statement-link"
            >
              {t("a11y.statementLink")}
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={t("a11y.openLabel")}
        className="flex h-12 w-12 items-center justify-center rounded-full bg-[#0F4C5C] text-white shadow-xl shadow-[#0F4C5C]/30 transition-[background-color,transform] duration-200 hover:scale-105 hover:bg-[#1A365D] sm:h-14 sm:w-14"
        data-testid="a11y-toggle-btn"
      >
        <Accessibility className="h-6 w-6 sm:h-7 sm:w-7" aria-hidden="true" />
      </button>
    </div>
  );
}
